import axios from 'axios';
import { createAsyncThunk, createSlice, isFulfilled, isPending, isRejected } from '@reduxjs/toolkit';
import { API_ROUTES } from '@/config/api-routes';
import { userIamUrl } from '@/config/user-iam';
import { changePasswordByUserId, } from '@/services/auth/changePasswordApi';
import { defaultValue } from '../model/user.model';
import { appendCriteriaEquals, appendPageable, readPagedRows, } from '@/shared/utils/spring-api';
import { resolveAsyncThunkErrorMessage, serializeAxiosError } from './reducer.utils';
/** IAM user API — create/update/delete still use `/user/api/admin/users`. */
const usersApiUrl = () => userIamUrl('/user/api/admin/users');
/** CRM list of UMS users with nested local CRM profile. */
const usersFromUmsApiUrl = API_ROUTES.usersFromUms;
const crmUsersApiUrl = API_ROUTES.users;
async function findCrmUserByLogin(login) {
    const trimmedLogin = login.trim();
    if (!trimmedLogin)
        return null;
    const q = new URLSearchParams();
    appendPageable(q, 0, 1, 'id,asc');
    appendCriteriaEquals(q, 'email', trimmedLogin);
    const response = await axios.get(`${crmUsersApiUrl}?${q.toString()}`);
    const { rows } = readPagedRows(response.data, response.headers);
    return rows[0] ?? null;
}
const initialState = {
    loading: false,
    errorMessage: null,
    users: [],
    user: defaultValue,
    updating: false,
    updateSuccess: false,
    totalItems: 0,
    userWithRoles: null,
    loadingUserWithRoles: false,
    passwordAssigning: false,
    passwordAssignSuccess: false,
};
export const getUsers = createAsyncThunk('userManagement/fetch_users', async ({ page, size, sort, username, email }) => {
    const trimmedUsername = username?.trim();
    const trimmedEmail = email?.trim();
    const params = {
        sort: sort ?? 'id,desc',
        size: size ?? 10,
        page: page ?? 0,
    };
    if (trimmedUsername)
        params.username = trimmedUsername;
    if (trimmedEmail)
        params.email = trimmedEmail;
    return axios.get(usersFromUmsApiUrl, { params });
}, { serializeError: serializeAxiosError });
export const getUser = createAsyncThunk('userManagement/fetch_user', async (id) => axios.get(`${usersApiUrl()}/${id}`), { serializeError: serializeAxiosError });
export const getUserCount = createAsyncThunk('userManagement/fetch_user_count', async () => axios.get(`${usersApiUrl()}/count`), { serializeError: serializeAxiosError });
export const createUser = createAsyncThunk('userManagement/create_user', async (entity) => axios.post(usersApiUrl(), entity), { serializeError: serializeAxiosError });
export const updateUser = createAsyncThunk('userManagement/update_user', async (entity) => {
    const result = await axios.put(usersApiUrl(), entity);
    return result;
}, { serializeError: serializeAxiosError });
export const patchUser = createAsyncThunk('userManagement/patch_user', async (entity) => {
    const { id, ...partial } = entity;
    const result = await axios.patch(`${usersApiUrl()}/${id}`, partial);
    return result;
}, { serializeError: serializeAxiosError });
/** Clears `phoneUser` on the CRM local profile. `{localUserId}` is `localUser.id`, not the UMS user id. */
export const unassignPhoneUser = createAsyncThunk('userManagement/unassign_phone_user', async (localUserId) => axios.patch(API_ROUTES.unassignPhoneUser(localUserId)), { serializeError: serializeAxiosError });
export const deleteUser = createAsyncThunk('userManagement/delete_user', async (login) => {
    const trimmedLogin = login.trim();
    if (!trimmedLogin) {
        throw new Error('User login is required.');
    }
    const crmUser = await findCrmUserByLogin(trimmedLogin);
    if (crmUser?.id != null) {
        await axios.patch(`${crmUsersApiUrl}/${crmUser.id}/soft-delete`);
    }
    await axios.delete(`${usersApiUrl()}/${encodeURIComponent(trimmedLogin)}`);
    return { login: trimmedLogin };
}, { serializeError: serializeAxiosError });
export const getUserWithRolesAndBranches = createAsyncThunk('userManagement/fetch_user_with_roles_and_branches', async (id) => axios.get(`${usersApiUrl()}/${id}/with-roles-and-branches`), { serializeError: serializeAxiosError });
export const assignUserPassword = createAsyncThunk('userManagement/assign_user_password', async (payload) => {
    await changePasswordByUserId(payload);
}, { serializeError: serializeAxiosError });
export const UserManagementSlice = createSlice({
    name: 'userManagement',
    initialState: initialState,
    reducers: {
        reset() {
            return initialState;
        },
        clearPasswordAssignState(state) {
            state.userWithRoles = null;
            state.loadingUserWithRoles = false;
            state.passwordAssigning = false;
            state.passwordAssignSuccess = false;
            state.errorMessage = null;
        },
        clearUserWithRoles(state) {
            state.userWithRoles = null;
            state.loadingUserWithRoles = false;
        },
        clearErrorMessage(state) {
            state.errorMessage = null;
        },
    },
    extraReducers(builder) {
        builder
            .addCase(getUser.fulfilled, (state, action) => {
            state.loading = false;
            state.user = action.payload.data;
        })
            .addCase(getUserCount.fulfilled, (state, action) => {
            state.totalItems = action.payload.data;
        })
            .addCase(deleteUser.fulfilled, (state) => {
            state.updating = false;
            state.updateSuccess = true;
            state.user = defaultValue;
        })
            .addCase(unassignPhoneUser.fulfilled, (state) => {
            state.updating = false;
            state.loading = false;
            state.updateSuccess = true;
        })
            .addCase(getUserWithRolesAndBranches.pending, (state) => {
            state.loadingUserWithRoles = true;
            state.errorMessage = null;
            state.userWithRoles = null;
        })
            .addCase(getUserWithRolesAndBranches.fulfilled, (state, action) => {
            state.loadingUserWithRoles = false;
            state.userWithRoles = action.payload.data;
        })
            .addCase(getUserWithRolesAndBranches.rejected, (state, action) => {
            state.loadingUserWithRoles = false;
            state.userWithRoles = null;
            state.errorMessage = resolveAsyncThunkErrorMessage(action.error);
        })
            .addCase(assignUserPassword.pending, (state) => {
            state.passwordAssigning = true;
            state.passwordAssignSuccess = false;
            state.errorMessage = null;
        })
            .addCase(assignUserPassword.fulfilled, (state) => {
            state.passwordAssigning = false;
            state.passwordAssignSuccess = true;
        })
            .addCase(assignUserPassword.rejected, (state, action) => {
            state.passwordAssigning = false;
            state.passwordAssignSuccess = false;
            state.errorMessage = resolveAsyncThunkErrorMessage(action.error);
        })
            .addMatcher(isFulfilled(getUsers), (state, action) => {
            state.loading = false;
            const { rows, total } = readPagedRows(action.payload.data, action.payload.headers);
            state.users = rows;
            state.totalItems = total;
        })
            .addMatcher(isFulfilled(createUser, updateUser, patchUser), (state, action) => {
            state.updating = false;
            state.loading = false;
            state.updateSuccess = true;
            state.user = action.payload.data;
        })
            .addMatcher(isPending(getUsers, getUser), (state) => {
            state.errorMessage = null;
            state.updateSuccess = false;
            state.loading = true;
        })
            .addMatcher(isPending(createUser, updateUser, patchUser, deleteUser, unassignPhoneUser), (state) => {
            state.errorMessage = null;
            state.updateSuccess = false;
            state.updating = true;
        })
            .addMatcher(isRejected(getUsers, getUser, getUserCount, createUser, updateUser, patchUser, deleteUser, unassignPhoneUser), (state, action) => {
            state.loading = false;
            state.updating = false;
            state.updateSuccess = false;
            state.errorMessage = resolveAsyncThunkErrorMessage(action.error);
        });
    },
});
export const { reset, clearPasswordAssignState, clearUserWithRoles, clearErrorMessage } = UserManagementSlice.actions;
export default UserManagementSlice.reducer;
